import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, Calendar, Settings, Bug } from 'lucide-react';
import { MovementScheduleTable } from '../components/movements/MovementScheduleTable';
import { EstimatedTimesModal } from '../components/movements/EstimatedTimesModal';
import { DefaultScheduleModal } from '../components/movements/DefaultScheduleModal';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { collection, query, getDocs, doc, getDoc, where } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import { Movement, EstimatedTime, DefaultSchedule } from '../types/movement';
import { useMovementFilters } from '../hooks/useMovementFilters';
import { calculateArrivalTime } from '../services/movements/timeUtils';

export const MovementSchedulePage = () => {
  const [isEstimatedTimesOpen, setIsEstimatedTimesOpen] = useState(false);
  const [isDefaultScheduleOpen, setIsDefaultScheduleOpen] = useState(false);
  const [movements, setMovements] = useState<Movement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [estimatedTimes, setEstimatedTimes] = useState<EstimatedTime[]>([]);
  const [defaultSchedules, setDefaultSchedules] = useState<DefaultSchedule[]>([]);
  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');
  const isAdmin = userRole === 'admin';

  // English fallback text
  const t = {
    pageTitle: 'Movement Schedule',
    pageSubtitle: 'Track pilgrim movements and countdown timers',
    estimatedTimes: 'Estimated Times',
    defaultSchedule: 'Default Schedule',
    todayMovements: 'Today\'s Movements',
    completedMovements: 'Completed Movements',
    upcomingMovements: 'Upcoming Movements',
    completionRate: 'Completion Rate'
  };

  // Initialize filters
  const { 
    filters, 
    handleFilterChange, 
    clearFilters, 
    filteredMovements 
  } = useMovementFilters(movements);

  // Load settings first
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Load estimated times
        const estimatedTimesDoc = await getDoc(doc(firestore, 'Settings', 'estimatedTimes'));
        if (estimatedTimesDoc.exists()) {
          setEstimatedTimes(estimatedTimesDoc.data().times);
        }

        // Load default schedules
        const defaultSchedulesDoc = await getDoc(doc(firestore, 'Settings', 'defaultSchedules'));
        if (defaultSchedulesDoc.exists()) {
          setDefaultSchedules(defaultSchedulesDoc.data().schedules);
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };

    loadSettings();
  }, []);

  // Get estimated time for a route
  const getEstimatedTime = (from: string, to: string): number => {
    const route = estimatedTimes.find(time => 
      time.from === from && time.to === to
    );
    return route?.hours || 2; // Default to 2 hours if not found
  };

  // Get default schedule
  const getDefaultSchedule = (type: string): { date?: string; time?: string } => {
  const schedule = defaultSchedules.find(s => s.name === 'وقت تحرك الفنادق');
  return {
    date: schedule?.date,
    time: schedule?.time || '12:00:00 PM'
  };
};



  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch all required data
        const [pilgrimsSnapshot, packagesSnapshot, flightsSnapshot] = await Promise.all([
          getDocs(collection(firestore, 'Pilgrims')),
          getDocs(collection(firestore, 'Packages')),
          getDocs(collection(firestore, 'Flights'))
        ]);

        const pilgrims = pilgrimsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const packages = packagesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const flights = flightsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // For organizers, get their operated pilgrims
        let operatedPilgrimIds: string[] = [];
        if (userRole === 'organizer' && userId) {
          const operationlistRef = collection(firestore, 'operationlist');
          const operationQuery = query(operationlistRef, where('operatorOrganizerId', '==', userId));
          const operationSnapshot = await getDocs(operationQuery);
          
          operatedPilgrimIds = operationSnapshot.docs.map(doc => doc.data().pilgrimId);
          console.log(`Organizer ${userId} operates ${operatedPilgrimIds.length} pilgrims`);
        }

        // Store pilgrims by PNR for quick lookup
        const pilgrimsByPNR = pilgrims.reduce((acc, pilgrim) => {
          if (pilgrim.PNR_NO) {
            if (!acc[pilgrim.PNR_NO]) {
              acc[pilgrim.PNR_NO] = [];
            }
            acc[pilgrim.PNR_NO].push(pilgrim);
          }
          return acc;
        }, {} as Record<string, any[]>);

        const generatedMovements: Movement[] = [];

        // Process arrival movements
        flights.forEach(flight => {
          const flightPilgrims = pilgrimsByPNR[flight.pnrNumber] || [];
          
          // For organizers, filter pilgrims to only show those they operate
          let filteredPilgrims = flightPilgrims;
          if (userRole === 'organizer' && operatedPilgrimIds.length > 0) {
            filteredPilgrims = flightPilgrims.filter(pilgrim => operatedPilgrimIds.includes(pilgrim.id));
            if (filteredPilgrims.length === 0) return; // Skip if no operated pilgrims in this flight
          }
          
          if (filteredPilgrims.length > 0) {
            const pkg = packages.find(p => p.name === filteredPilgrims[0].package_nickName);
            if (pkg) {
              // Collect all hotels and sort by start date
              const allHotels = [
                pkg.shiftingMakkahHotel ? {
                  hotelName: pkg.shiftingMakkahHotel,
                  city: 'makkah' as const,
                  startDate: pkg.shiftingMakkahStartDate,
                  endDate: pkg.shiftingMakkahEndDate,
                } : null,
                {
                  hotelName: pkg.mainMakkahHotel,
                  city: 'makkah' as const,
                  startDate: pkg.mainMakkahStartDate,
                  endDate: pkg.mainMakkahEndDate,
                },
                pkg.madinahHotel ? {
                  hotelName: pkg.madinahHotel,
                  city: 'madinah' as const,
                  startDate: pkg.madinahStartDate,
                  endDate: pkg.madinahEndDate,
                } : null
              ].filter(Boolean); // Remove null entries
              
              // Sort hotels by start date
              const hotelStays = allHotels.sort((a, b) => {
                const aDate = new Date(a.startDate.split('/').reverse().join('-'));
                const bDate = new Date(b.startDate.split('/').reverse().join('-'));
                return aDate.getTime() - bDate.getTime();
              });
              
              // First hotel is the earliest by date
              const firstHotel = hotelStays[0];
              
              if (firstHotel) {
                const estimatedHours = getEstimatedTime(flight.outboundArrival, firstHotel.hotelName);
                const arrivalTime = calculateArrivalTime(flight.outboundArrivalTime, estimatedHours);

                generatedMovements.push({
                  id: `arrival-${flight.id}`,
                  type: 'arrival',
                  name: 'قدوم الحجاج',
                  from: {
                    type: 'airport',
                    name: flight.outboundArrival,
                    city: flight.outboundArrival.toLowerCase() as 'jeddah' | 'madinah' | 'taif'
                  },
                  to: {
                    type: 'hotel',
                    name: firstHotel.hotelName,
                    city: firstHotel.city
                  },
                  departureDate: flight.arrivalDate,
                  departureTime: flight.outboundArrivalTime,
                  estimatedHours,
                  arrivalDate: flight.arrivalDate,
                  arrivalTime,
                  pilgrimsCount: filteredPilgrims.length,
                  pilgrims: filteredPilgrims
                });
              }
            }
          }
        });

        // Process hotel transfers
        packages.forEach(pkg => {
          let packagePilgrims = pilgrims.filter(p => p.package_nickName === pkg.name);
          
          // For organizers, filter pilgrims to only show those they operate
          if (userRole === 'organizer' && operatedPilgrimIds.length > 0) {
            packagePilgrims = packagePilgrims.filter(pilgrim => operatedPilgrimIds.includes(pilgrim.id));
            if (packagePilgrims.length === 0) return; // Skip if no operated pilgrims in this package
          }
          
          if (packagePilgrims.length > 0) {
            // Collect all hotels and sort by start date
            const allHotels = [
              pkg.shiftingMakkahHotel ? {
                hotelName: pkg.shiftingMakkahHotel,
                city: 'makkah' as const,
                startDate: pkg.shiftingMakkahStartDate,
                endDate: pkg.shiftingMakkahEndDate,
              } : null,
              {
                hotelName: pkg.mainMakkahHotel,
                city: 'makkah' as const,
                startDate: pkg.mainMakkahStartDate,
                endDate: pkg.mainMakkahEndDate,
              },
              pkg.madinahHotel ? {
                hotelName: pkg.madinahHotel,
                city: 'madinah' as const,
                startDate: pkg.madinahStartDate,
                endDate: pkg.madinahEndDate,
              } : null
            ].filter(Boolean); // Remove null entries
            
            // Sort hotels by start date
            const hotelStays = allHotels.sort((a, b) => {
              const aDate = new Date(a.startDate.split('/').reverse().join('-'));
              const bDate = new Date(b.startDate.split('/').reverse().join('-'));
              return aDate.getTime() - bDate.getTime();
            });
            
            // Process transfers between hotels based on date order
            if (hotelStays.length > 1) {
              for (let i = 0; i < hotelStays.length - 1; i++) {
                const fromHotel = hotelStays[i];
                const toHotel = hotelStays[i + 1];
                
                const defaultTime = getDefaultSchedule('hotel_transfer').time || '12:00:00 PM';
                const estimatedHours = getEstimatedTime(fromHotel.hotelName, toHotel.hotelName);
                const arrivalTime = calculateArrivalTime(defaultTime, estimatedHours);
                
                // Determine movement type based on cities
                const movementType = 
  fromHotel.city === 'makkah' && toHotel.city === 'madinah' ? 'hotel_transfer_madinah' :
  fromHotel.city === 'madinah' && toHotel.city === 'makkah' ? 'hotel_transfer_makkah' :
  'hotel_transfer';

const movementName = 
  movementType === 'hotel_transfer_madinah' ? 'تحرك إلى المدينة' :
  movementType === 'hotel_transfer_makkah' ? 'تحرك إلى مكة' :
  'تحرك بين الفنادق';

                
                generatedMovements.push({
                  id: `transfer-${pkg.id}-${i}`,
                  type: movementType,
                  name: movementName,
                  from: {
                    type: 'hotel',
                    name: fromHotel.hotelName,
                    city: fromHotel.city
                  },
                  to: {
                    type: 'hotel',
                    name: toHotel.hotelName,
                    city: toHotel.city
                  },
                  departureDate: toHotel.startDate,
                  departureTime: defaultTime,
                  estimatedHours,
                  arrivalDate: toHotel.startDate,
                  arrivalTime,
                  pilgrimsCount: packagePilgrims.length,
                  pilgrims: packagePilgrims
                });
              }
            }
          }
        });

        // Process departure movements
        flights.forEach(flight => {
          const flightPilgrims = pilgrimsByPNR[flight.pnrNumber] || [];
          
          // For organizers, filter pilgrims to only show those they operate
          let filteredPilgrims = flightPilgrims;
          if (userRole === 'organizer' && operatedPilgrimIds.length > 0) {
            filteredPilgrims = flightPilgrims.filter(pilgrim => operatedPilgrimIds.includes(pilgrim.id));
            if (filteredPilgrims.length === 0) return; // Skip if no operated pilgrims in this flight
          }
          
          if (filteredPilgrims.length > 0) {
            const pkg = packages.find(p => p.name === filteredPilgrims[0].package_nickName);
            if (pkg) {
              // Collect all hotels and sort by end date (descending)
              const allHotels = [
                pkg.shiftingMakkahHotel ? {
                  hotelName: pkg.shiftingMakkahHotel,
                  city: 'makkah' as const,
                  startDate: pkg.shiftingMakkahStartDate,
                  endDate: pkg.shiftingMakkahEndDate,
                } : null,
                {
                  hotelName: pkg.mainMakkahHotel,
                  city: 'makkah' as const,
                  startDate: pkg.mainMakkahStartDate,
                  endDate: pkg.mainMakkahEndDate,
                },
                pkg.madinahHotel ? {
                  hotelName: pkg.madinahHotel,
                  city: 'madinah' as const,
                  startDate: pkg.madinahStartDate,
                  endDate: pkg.madinahEndDate,
                } : null
              ].filter(Boolean); // Remove null entries
              
              // Sort hotels by end date (descending)
              const hotelStays = allHotels.sort((a, b) => {
                const aDate = new Date(a.endDate.split('/').reverse().join('-'));
                const bDate = new Date(b.endDate.split('/').reverse().join('-'));
                return bDate.getTime() - aDate.getTime();
              });
              
              // Last hotel is the one with the latest end date
              const lastHotel = hotelStays[0];
              
              if (lastHotel) {
                const estimatedHours = getEstimatedTime(lastHotel.hotelName, flight.returnDeparture);
                const departureTime = calculateArrivalTime(flight.returnDepartureTime, -estimatedHours);

                generatedMovements.push({
                  id: `departure-${flight.id}`,
                  type: 'departure',
                  name: 'رحيل الحجاج',
                  from: {
                    type: 'hotel',
                    name: lastHotel.hotelName,
                    city: lastHotel.city
                  },
                  to: {
                    type: 'airport',
                    name: flight.returnDeparture,
                    city: flight.returnDeparture.toLowerCase() as 'jeddah' | 'madinah' | 'taif'
                  },
                  departureDate: flight.returnDate,
                  departureTime,
                  estimatedHours,
                  arrivalDate: flight.returnDate,
                  arrivalTime: flight.returnDepartureTime,
                  pilgrimsCount: filteredPilgrims.length,
                  pilgrims: filteredPilgrims
                });
              }
            }
          }
        });

        // Sort movements by date
        const sortedMovements = generatedMovements.sort((a, b) => {
          const dateA = new Date(a.departureDate.split('/').reverse().join('-'));
          const dateB = new Date(b.departureDate.split('/').reverse().join('-'));
          return dateA.getTime() - dateB.getTime();
        });

        setMovements(sortedMovements);
      } catch (error: any) {
        console.error('Error fetching data:', error);
        setError(error.message || 'Failed to load movements');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [estimatedTimes, defaultSchedules, userRole, userId]); // Re-run when settings change or user role/id changes

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  // Helper function to properly parse dates in DD/MM/YYYY format
  const parseDate = (dateStr: string): Date => {
    const [day, month, year] = dateStr.split('/').map(Number);
    return new Date(year, month - 1, day);
  };
  
  // Get today's date and reset time to start of day
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Calculate stats with correct date parsing
  const todayStr = today.getDate().toString().padStart(2, '0') + '/' + 
                  (today.getMonth() + 1).toString().padStart(2, '0') + '/' + 
                  today.getFullYear();
                  
  const todayMovements = filteredMovements.filter(m => m.departureDate === todayStr);
  
  const completedMovements = filteredMovements.filter(m => {
    const movementDate = parseDate(m.departureDate);
    return movementDate < today;
  });
  
  const upcomingMovements = filteredMovements.filter(m => {
    const movementDate = parseDate(m.departureDate);
    return movementDate >= today;
  });
  
  const completionRate = filteredMovements.length > 0 
    ? Math.round((completedMovements.length / filteredMovements.length) * 100) 
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-50 rounded-lg">
            <Clock className="text-primary-500" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Movement Schedule</h1>
            <p className="text-gray-500 mt-1">{t.pageSubtitle}</p>
          </div>
        </div>

        {/* Configuration buttons */}
        <div className="flex gap-3">
          {/* Test Button - For all users */}
         

          {/* Only show configuration buttons for admin users */}
          {isAdmin && (
            <>
              <button
                onClick={() => setIsEstimatedTimesOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-600 
                        rounded-lg hover:bg-primary-100 transition-colors"
              >
                <Clock size={20} />
                <span>{t.estimatedTimes}</span>
              </button>

              <button
                onClick={() => setIsDefaultScheduleOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-600 
                        rounded-lg hover:bg-primary-100 transition-colors"
              >
                <Calendar size={20} />
                <span>{t.defaultSchedule}</span>
              </button>
            </>
          )}
        </div>
      </div>

   
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Clock className="text-blue-500" size={20} />
            </div>
            <h3 className="font-medium">{t.todayMovements}</h3>
          </div>
          <p className="text-2xl font-bold text-blue-600">{todayMovements.length}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-green-50 rounded-lg">
              <Clock className="text-green-500" size={20} />
            </div>
            <h3 className="font-medium">{t.completedMovements}</h3>
          </div>
          <p className="text-2xl font-bold text-green-600">{completedMovements.length}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-yellow-50 rounded-lg">
              <Clock className="text-yellow-500" size={20} />
            </div>
            <h3 className="font-medium">{t.upcomingMovements}</h3>
          </div>
          <p className="text-2xl font-bold text-yellow-600">{upcomingMovements.length}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary-50 rounded-lg">
              <Settings className="text-primary-500" size={20} />
            </div>
            <h3 className="font-medium">{t.completionRate}</h3>
          </div>
          <p className="text-2xl font-bold text-primary-600">{completionRate}%</p>
        </motion.div>
      </div>

      {/* Main Table */}
      <MovementScheduleTable 
        movements={filteredMovements}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={clearFilters}
      />

      {/* Modals - only rendered if user is admin since only they can open them */}
      {isAdmin && (
        <>
          <EstimatedTimesModal 
            isOpen={isEstimatedTimesOpen}
            onClose={() => setIsEstimatedTimesOpen(false)}
          />

          <DefaultScheduleModal
            isOpen={isDefaultScheduleOpen}
            onClose={() => setIsDefaultScheduleOpen(false)}
          />
        </>
      )}
    </div>
  );
};